import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import { signupSchema } from "@/lib/schemas";
import { useSEO } from "@/lib/useSEO";
import { Gift, CheckCircle2 } from "lucide-react";

// Tiny helper: inline field-level error message (uppercase tracking like other labels).
function FieldError({ children, testId }) {
  if (!children) return null;
  return (
    <div data-testid={testId} className="mt-1.5 text-[11px] text-[#d94f3d] flex items-center gap-1">
      {children}
    </div>
  );
}

export default function Signup() {
  const { user, setUserDirect } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").trim().toUpperCase();

  useSEO({
    title: "Create your workspace — Hearth & Crate",
    description: "Start your free 14-day Hearth & Crate trial. No card required. Set up FSA-ready temperature logs, audits, batch tracking, invoicing and staff records in under 60 seconds.",
    // Canonicalise plan variants to the base /signup URL to fix the duplicate-content warning.
    canonical: "https://hearthpro.co.uk/signup",
  });

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      business_name: "",
      referral_code: refFromUrl,
    },
    mode: "onTouched",
  });

  const referralCode = watch("referral_code") || "";
  const [referrer, setReferrer] = useState(null);
  const [refLooking, setRefLooking] = useState(false);

  useEffect(() => {
    if (user && user.email) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Resolve referral code (debounced).
  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    if (!code) { setReferrer(null); return; }
    setRefLooking(true);
    const handle = setTimeout(() => {
      api.post("/referrals/lookup", { code })
        .then(({ data }) => setReferrer(data))
        .catch(() => setReferrer({ invalid: true }))
        .finally(() => setRefLooking(false));
    }, 350);
    return () => clearTimeout(handle);
  }, [referralCode]);

  const onSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (!payload.referral_code) delete payload.referral_code;
      const { data } = await api.post("/auth/signup", payload);
      setUserDirect(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Failed";
      // 409 = email already exists — surface it on the email field for clearer UX.
      if (err.response?.status === 409) {
        setError("email", { type: "server", message: "An account with this email already exists" });
      } else {
        setError("root.server", { type: "server", message: detail });
      }
    }
  };

  const rootServerError = errors.root?.server?.message;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f7f5f0]">
      <div className="flex flex-col px-8 sm:px-16 py-10 lg:py-20">
        <div className="font-display text-xl font-medium tracking-tight">
          Hearth<span className="text-[#c0612f]">.</span>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#8a8476] mb-4">
              Create account
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-none">
              Start running<br />
              <span className="text-[#4e7b5b]">a tighter ship.</span>
            </h1>
            <p className="mt-4 text-sm text-[#57534e] leading-relaxed">
              Create your own private workspace. You become the owner — invite your team from inside.
            </p>

            <GoogleButton className="mt-8" />
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#e5e2d9]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#8a8476]">or with email</span>
              <div className="flex-1 h-px bg-[#e5e2d9]" />
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} data-testid="signup-form" noValidate>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Business name</Label>
                <Input
                  data-testid="signup-business-input"
                  placeholder="e.g. The Olive Branch Café"
                  className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]"
                  aria-invalid={!!errors.business_name}
                  {...register("business_name")}
                />
                <FieldError testId="signup-business-error">{errors.business_name?.message}</FieldError>
                <div className="text-[10px] text-[#8a8476] mt-1.5">This is your private workspace — only you and people you invite will see its data.</div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Your full name</Label>
                <Input
                  data-testid="signup-name-input"
                  className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]"
                  aria-invalid={!!errors.name}
                  {...register("name")}
                />
                <FieldError testId="signup-name-error">{errors.name?.message}</FieldError>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Email</Label>
                <Input
                  type="email"
                  data-testid="signup-email-input"
                  className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]"
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
                <FieldError testId="signup-email-error">{errors.email?.message}</FieldError>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Password</Label>
                <Input
                  type="password"
                  data-testid="signup-password-input"
                  className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]"
                  aria-invalid={!!errors.password}
                  {...register("password")}
                />
                <FieldError testId="signup-password-error">{errors.password?.message}</FieldError>
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476] flex items-center gap-1.5">
                  <Gift className="w-3 h-3" strokeWidth={1.8} /> Referral code <span className="normal-case tracking-normal text-[#a8a294]">(optional)</span>
                </Label>
                <Input
                  data-testid="signup-referral-input"
                  placeholder="KITCHEN-XXXX"
                  className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px] uppercase font-mono"
                  {...register("referral_code", {
                    onChange: (e) => {
                      // Force uppercase as the user types.
                      const v = e.target.value.toUpperCase();
                      if (v !== e.target.value) e.target.value = v;
                    },
                  })}
                />
                <FieldError testId="signup-referral-error">{errors.referral_code?.message}</FieldError>
                {referralCode && referrer?.valid && (
                  <div data-testid="signup-referral-valid" className="mt-2 flex items-center gap-2 text-xs text-[#4e7b5b] bg-[#e8f0e8] px-2.5 py-1.5 rounded-[3px]">
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                    <span>Referred by <b>{referrer.referrer_name}</b> — you both get 1 free month when you subscribe.</span>
                  </div>
                )}
                {referralCode && referrer?.invalid && !refLooking && (
                  <div data-testid="signup-referral-invalid" className="mt-2 text-xs text-[#c0612f]">
                    We don't recognise that code — you can still sign up without it.
                  </div>
                )}
              </div>

              {rootServerError && (
                <div data-testid="signup-error" className="text-sm text-[#d94f3d] bg-[#fbeae8] px-3 py-2 rounded-[3px]">
                  {rootServerError}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="signup-submit-button"
                className="w-full h-11 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] font-medium"
              >
                {isSubmitting ? "Creating…" : "Create account"}
              </Button>

              <div className="text-sm text-[#57534e] mt-4">
                Already have an account?{" "}
                <Link to="/login" data-testid="signup-to-login" className="text-[#1b3b36] underline underline-offset-2">
                  Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="text-xs text-[#8a8476] flex items-center gap-4">
          <span>© {new Date().getFullYear()} Hearth Operations</span>
          <Link to="/legal/terms" className="hover:text-[#1b3b36]" data-testid="signup-link-terms">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-[#1b3b36]" data-testid="signup-link-privacy">Privacy</Link>
        </div>
      </div>

      <div className="relative hidden lg:block overflow-hidden grain">
        <img
          src="https://images.pexels.com/photos/7223295/pexels-photo-7223295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Fresh produce"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1b3b36]/65 via-[#1b3b36]/25 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.32em] text-white/70 mb-2">Setup — 60 seconds</div>
          <div className="font-display text-3xl font-medium tracking-tight max-w-md leading-tight">
            Six modules. One source of truth. Inspection-ready by tomorrow morning.
          </div>
        </div>
      </div>
    </div>
  );
}
